import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Video, CheckCircle, Lock, ChevronRight, Award, Play, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { BrandWatermark } from '@/components/BrandWatermark';
import { UserBadges } from '@/components/UserBadges';
import { useAuth } from '@/context/AuthContext';
import CertificationExam from '@/components/CertificationExam';
import { DiplomaGenerator } from '@/components/DiplomaGenerator';
import { Leaderboard } from '@/components/Leaderboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'shorts', label: 'Shorts', badge: '/badge_shorts.png' },
  { key: 'basicos', label: 'Básicos', badge: '/badge_basicos.png' },
  { key: 'avanzados', label: 'Avanzados', badge: '/badge_avanzados.png' },
  { key: 'psicotrading', label: 'Psicotrading', badge: '/badge_psicotrading.png' },
  { key: 'algoritmos', label: 'Algoritmos', badge: '/badge_algoritmos.png' },
];

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:v=|\/embed\/|\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : url.split('/').pop()?.split('?')[0];
};

const getVimeoId = (url) => {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

function VideoPlayer({ video, onComplete, alreadyWatched }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const checkInterval = useRef(null);
  const maxWatchedRef = useRef(0);
  const [progress, setProgress] = useState(alreadyWatched ? 100 : 0);
  const [ended, setEnded] = useState(!!alreadyWatched);
  const isVimeo = video?.platform === 'vimeo';
  const skipProtection = !alreadyWatched;

  useEffect(() => {
    if (!video || !containerRef.current) return;
    maxWatchedRef.current = 0;
    setProgress(0);
    setEnded(false);

    if (isVimeo) {
      initVimeo();
    } else {
      initYouTube();
    }

    function initYouTube() {
      const videoId = getYouTubeId(video.url);
      if (!videoId) return;
      if (!window.YT) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
        window.onYouTubeIframeAPIReady = () => createYTPlayer(videoId);
      } else {
        createYTPlayer(videoId);
      }
    }

    function createYTPlayer(id) {
      if (playerRef.current?.destroy) {
        try { playerRef.current.destroy(); } catch {}
      }
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: id,
        playerVars: { autoplay: 0, controls: 1, disablekb: skipProtection ? 1 : 0, modestbranding: 1, rel: 0, fs: 1 },
        events: {
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              markComplete();
            }
            if (event.data === window.YT.PlayerState.PLAYING) {
              startYTTracking();
            }
          }
        }
      });
    }

    function startYTTracking() {
      if (checkInterval.current) clearInterval(checkInterval.current);
      checkInterval.current = setInterval(() => {
        if (playerRef.current?.getCurrentTime && playerRef.current?.getDuration) {
          const current = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          if (duration > 0) {
            // Anti-skip only if video not yet completed
            if (skipProtection && current > maxWatchedRef.current + 3) {
              playerRef.current.seekTo(maxWatchedRef.current, true);
              return;
            }
            if (current > maxWatchedRef.current) maxWatchedRef.current = current;
            trackProgress(maxWatchedRef.current, duration);
          }
        }
      }, 1500);
    }

    function initVimeo() {
      const vimeoId = getVimeoId(video.url);
      if (!vimeoId) return;

      // Load Vimeo Player SDK if needed
      const loadAndCreate = () => {
        if (playerRef.current?.destroy) {
          try { playerRef.current.destroy(); } catch {}
        }
        // Create iframe
        containerRef.current.innerHTML = '';
        const iframe = document.createElement('iframe');
        iframe.src = `https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&autoplay=0`;
        iframe.style.cssText = 'width:100%;height:100%;border:none;';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        containerRef.current.appendChild(iframe);

        const player = new window.Vimeo.Player(iframe);
        playerRef.current = player;

        player.on('ended', () => markComplete());
        player.on('play', () => startVimeoTracking(player));
      };

      if (!window.Vimeo) {
        const tag = document.createElement('script');
        tag.src = 'https://player.vimeo.com/api/player.js';
        tag.onload = loadAndCreate;
        document.head.appendChild(tag);
      } else {
        loadAndCreate();
      }
    }

    function startVimeoTracking(player) {
      if (checkInterval.current) clearInterval(checkInterval.current);
      checkInterval.current = setInterval(async () => {
        try {
          const current = await player.getCurrentTime();
          const duration = await player.getDuration();
          if (duration > 0) {
            // Anti-skip only if video not yet completed
            if (skipProtection && current > maxWatchedRef.current + 3) {
              player.setCurrentTime(maxWatchedRef.current);
              return;
            }
            if (current > maxWatchedRef.current) maxWatchedRef.current = current;
            trackProgress(maxWatchedRef.current, duration);
          }
        } catch {}
      }, 1500);
    }

    function trackProgress(watched, duration) {
      const pct = Math.round((watched / duration) * 100);
      setProgress(pct);
      if (pct >= 95 && !ended) {
        markComplete();
      }
    }

    function markComplete() {
      setEnded(true);
      setProgress(100);
      if (checkInterval.current) clearInterval(checkInterval.current);
      onComplete();
    }

    return () => {
      if (checkInterval.current) clearInterval(checkInterval.current);
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch {}
        playerRef.current = null;
      }
    };
  }, [video?.id]);

  return (
    <div className="glass-panel rounded-lg overflow-hidden" data-testid="video-player">
      <div style={{ aspectRatio: '16/9', background: '#000' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </div>
      {/* Progress bar */}
      <div className="h-1 bg-black/50">
        <div className={`h-full transition-all duration-500 ${ended ? 'bg-green-500' : 'bg-emerald-500'}`} style={{ width: `${progress}%` }} />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{video?.title}</h3>
            {video?.description && <p className="text-emerald-100/50 text-sm mt-1">{video.description}</p>}
          </div>
          {ended && <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0 ml-3" />}
        </div>
      </div>
    </div>
  );
}

export default function Videos() {
  const { user } = useAuth();
  const [allVideos, setAllVideos] = useState([]);
  const [progress, setProgress] = useState({ watched_video_ids: [], badges: [], progress_by_category: {} });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('shorts');
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [badgeJustEarned, setBadgeJustEarned] = useState(null);
  const [showExam, setShowExam] = useState(false);
  const [examPassed, setExamPassed] = useState(false);
  const [showDiploma, setShowDiploma] = useState(false);
  const [examData, setExamData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, pRes] = await Promise.all([
        axios.get(`${API}/videos`),
        axios.get(`${API}/video-progress`)
      ]);
      setAllVideos(vRes.data);
      setProgress(pRes.data);
      // Check exam status
      try {
        const examRes = await axios.get(`${API}/exam/status`);
        setExamPassed(examRes.data.passed);
        if (examRes.data.passed) setExamData(examRes.data);
      } catch {}
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const catVideos = allVideos
    .filter(v => v.category === activeCategory)
    .sort((a, b) => (a.order || 0) - (b.order || 0) || new Date(a.created_at) - new Date(b.created_at));

  const watchedIds = new Set(progress.watched_video_ids || []);

  // Find the first unwatched video index
  const firstUnwatched = catVideos.findIndex(v => !watchedIds.has(v.id));
  const activeVideo = catVideos.find(v => v.id === activeVideoId) || catVideos[firstUnwatched >= 0 ? firstUnwatched : 0];

  const isVideoUnlocked = (index) => {
    if (index === 0) return true;
    return watchedIds.has(catVideos[index - 1]?.id);
  };

  const handleVideoComplete = async () => {
    if (!activeVideo) return;
    try {
      const res = await axios.post(`${API}/video-progress/${activeVideo.id}`);
      if (res.data.status === 'badge_earned') {
        setBadgeJustEarned(res.data.badge);
        toast.success(`Insignia desbloqueada: ${res.data.badge.toUpperCase()}`);
      }
      await fetchData();
      // Auto-advance to next video
      const currentIdx = catVideos.findIndex(v => v.id === activeVideo.id);
      if (currentIdx < catVideos.length - 1) {
        setTimeout(() => setActiveVideoId(catVideos[currentIdx + 1].id), 1500);
      }
    } catch {}
  };

  const catProgress = progress.progress_by_category?.[activeCategory] || { total: 0, watched: 0 };
  const catPct = catProgress.total > 0 ? Math.round((catProgress.watched / catProgress.total) * 100) : 0;
  const catBadgeEarned = progress.badges?.includes(activeCategory);

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      <BrandWatermark />

      <div className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Header with badges */}
          <div className="mb-6 flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }} data-testid="videos-title">
                  CONTENIDO EDUCATIVO
                </h1>
                <UserBadges badges={progress.badges || []} size="md" />
              </div>
              <p className="text-emerald-100/70 mt-1">Completa cada sección para desbloquear insignias</p>
            </div>
          </div>

          {/* Category Tabs with progress */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="video-categories">
            {CATEGORIES.map((cat) => {
              const cp = progress.progress_by_category?.[cat.key] || { total: 0, watched: 0 };
              const pct = cp.total > 0 ? Math.round((cp.watched / cp.total) * 100) : 0;
              const earned = progress.badges?.includes(cat.key);
              return (
                <button
                  key={cat.key}
                  onClick={() => { setActiveCategory(cat.key); setActiveVideoId(null); }}
                  className={`relative px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-all ${
                    activeCategory === cat.key
                      ? 'bg-emerald-500 text-black'
                      : earned
                        ? 'bg-green-500/10 border border-green-500/40 text-green-400'
                        : 'bg-black/40 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                  }`}
                  data-testid={`category-${cat.key}`}
                >
                  <span className="flex items-center space-x-1.5">
                    {earned && <CheckCircle className="h-3.5 w-3.5" />}
                    <span>{cat.label}</span>
                    {cp.total > 0 && !earned && (
                      <span className="text-[10px] opacity-60">{cp.watched}/{cp.total}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Category Progress Bar */}
          {catProgress.total > 0 && (
            <div className="glass-panel rounded-lg p-3 mb-6 flex items-center space-x-4" data-testid="category-progress">
              <img src={CATEGORIES.find(c => c.key === activeCategory)?.badge} alt="" className={`w-12 h-12 rounded-full object-cover ${catBadgeEarned ? '' : 'grayscale opacity-40'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-emerald-50 font-bold text-sm">{catProgress.watched} de {catProgress.total} videos completados</p>
                  <p className={`text-sm font-bold ${catBadgeEarned ? 'text-green-400' : 'text-emerald-400'}`}>{catPct}%</p>
                </div>
                <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${catBadgeEarned ? 'bg-green-500' : 'bg-emerald-500'}`} style={{ width: `${catPct}%` }} />
                </div>
              </div>
              {catBadgeEarned && <Award className="h-5 w-5 text-green-400 flex-shrink-0" />}
            </div>
          )}

          {/* Certification Banner - all badges earned */}
          {progress.badges?.length === 5 && (
            <div className={`glass-panel rounded-lg p-4 mb-6 border-2 ${examPassed ? 'border-green-500/40' : 'border-yellow-500/40'}`} data-testid="certification-banner">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${examPassed ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                    <GraduationCap className={`h-7 w-7 ${examPassed ? 'text-green-400' : 'text-yellow-400'}`} />
                  </div>
                  <div>
                    <p className={`font-bold text-lg ${examPassed ? 'text-green-400' : 'text-yellow-400'}`} style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                      {examPassed ? 'CERTIFICACIÓN COMPLETADA' : 'EVALUACIÓN DE CERTIFICACIÓN'}
                    </p>
                    <p className="text-emerald-100/50 text-sm">
                      {examPassed
                        ? 'Has aprobado la evaluación. Descarga tu diploma.'
                        : 'Completaste todos los videos. Presenta el examen (80% para aprobar).'
                      }
                    </p>
                  </div>
                </div>
                {examPassed ? (
                  <button onClick={() => setShowDiploma(true)} className="bg-green-500 text-black px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-green-400 transition-all flex items-center space-x-2" data-testid="download-diploma">
                    <Award className="h-4 w-4" /><span>Ver Diploma</span>
                  </button>
                ) : (
                  <button onClick={() => setShowExam(true)} className="bg-yellow-500 text-black px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-yellow-400 transition-all flex items-center space-x-2" data-testid="start-exam">
                    <GraduationCap className="h-4 w-4" /><span>Presentar Examen</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent" />
              <p className="text-emerald-100/70 mt-4">Cargando videos...</p>
            </div>
          ) : catVideos.length === 0 ? (
            <div className="glass-panel rounded-lg p-12 text-center" data-testid="no-videos">
              <Video className="h-16 w-16 text-emerald-500/50 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-emerald-100">No hay videos en esta categoría</h3>
              <p className="text-emerald-100/60">Los nuevos videos aparecerán aquí cuando sean publicados.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Video Player (main area) */}
              <div className="lg:col-span-2">
                {activeVideo && (
                  <VideoPlayer key={activeVideo.id} video={activeVideo} onComplete={handleVideoComplete} alreadyWatched={watchedIds.has(activeVideo.id)} />
                )}
              </div>

              {/* Video List (sidebar) */}
              <div className="glass-panel rounded-lg overflow-hidden" data-testid="video-list">
                <div className="p-3 border-b border-emerald-500/20">
                  <h3 className="text-sm font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    {CATEGORIES.find(c => c.key === activeCategory)?.label.toUpperCase()} ({catVideos.length} videos)
                  </h3>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {catVideos.map((v, idx) => {
                    const watched = watchedIds.has(v.id);
                    const unlocked = isVideoUnlocked(idx);
                    const isCurrent = activeVideo?.id === v.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => { if (unlocked) setActiveVideoId(v.id); }}
                        disabled={!unlocked}
                        className={`w-full text-left flex items-center space-x-3 px-3 py-2.5 border-b border-emerald-500/10 transition-all ${
                          isCurrent ? 'bg-emerald-500/10' : unlocked ? 'hover:bg-emerald-500/5' : 'opacity-40 cursor-not-allowed'
                        }`}
                        data-testid={`video-item-${v.id}`}
                      >
                        <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold">
                          {watched ? (
                            <CheckCircle className="h-5 w-5 text-green-400" />
                          ) : unlocked ? (
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isCurrent ? 'bg-emerald-500 text-black' : 'bg-emerald-500/20 text-emerald-400'}`}>
                              {isCurrent ? <Play className="h-3 w-3" /> : idx + 1}
                            </div>
                          ) : (
                            <Lock className="h-4 w-4 text-emerald-100/30" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs truncate ${isCurrent ? 'text-emerald-400 font-bold' : watched ? 'text-emerald-100/50' : 'text-emerald-100/80'}`}>
                            {v.title}
                          </p>
                        </div>
                        {isCurrent && <ChevronRight className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="mt-8">
            <Leaderboard />
          </div>

        </div>
      </div>

      {/* Badge Earned Modal */}
      {badgeJustEarned && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" data-testid="badge-modal">
          <div className="text-center max-w-sm">
            <div className="relative inline-block mb-4">
              <img src={CATEGORIES.find(c => c.key === badgeJustEarned)?.badge} alt="" className="w-40 h-40 rounded-full object-cover animate-bounce" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-400 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              INSIGNIA DESBLOQUEADA
            </h2>
            <p className="text-emerald-50 text-lg font-bold mb-1">{badgeJustEarned.toUpperCase()}</p>
            <p className="text-emerald-100/50 text-sm mb-6">Has completado todos los videos de esta sección</p>
            <button
              onClick={() => setBadgeJustEarned(null)}
              className="bg-emerald-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-emerald-400 transition-all"
              data-testid="badge-modal-close"
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* Certification Exam Modal */}
      {showExam && (
        <CertificationExam
          onPass={() => { setShowExam(false); setExamPassed(true); fetchData(); }}
          onClose={() => setShowExam(false)}
        />
      )}

      {/* Diploma Generator Modal */}
      {showDiploma && (
        <DiplomaGenerator
          studentName={user?.name || user?.email?.split('@')[0] || 'Estudiante'}
          date={examData?.passed_at}
          certificateNumber={`TA-${(user?.id || '').slice(-6).toUpperCase()}`}
          onClose={() => setShowDiploma(false)}
        />
      )}
    </div>
  );
}
